import { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import { useGetStockPageMutation } from '../../api/reports/ReportApi';
import { MasterRow } from '../../types/master';
import { exportToExcel } from '../../utils/exportExcel';
import { longDate } from '../../utils/dates';
import { readable } from '../../utils/text';

const statusColour: Record<string, 'success' | 'default' | 'error' | 'warning'> = {
  AVAILABLE: 'success',
  SOLD: 'default',
  // Kept so an older row still renders if these are ever written.
  DAMAGED: 'error',
  RETURNED_TO_SUPPLIER: 'warning',
};

/** Every piece the shop has ever taken in, searchable by serial number, model or brand. */
const StockReport = () => {
  const [searchValue, setSearchValue] = useState('');
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState<MasterRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [getStockPage] = useGetStockPageMutation();

  const load = () => {
    if (timeout.current) clearTimeout(timeout.current);
    setLoading(true);
    timeout.current = setTimeout(async () => {
      try {
        const response = await getStockPage({
          searchValue,
          status,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }).unwrap();
        setRows(response?.data || []);
        setRowCount(response?.totalCount || 0);
      } catch {
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  useEffect(() => {
    load();
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, status, paginationModel.page, paginationModel.pageSize]);

  /**
   * Any change of filter puts the list back on the first page.
   *
   * <p>Page 4 of the old results is not page 4 of the new ones - usually it is past
   * the end of them, and the shop is shown an empty grid for a search that matched.
   */
  useEffect(() => {
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, status]);

  const columns: GridColDef[] = [
    // Product first, then the serial number that belongs to it - the shop reads
    // "which phone" before "which piece of it".
    { field: 'brandName', headerName: 'Brand', flex: 1, minWidth: 110, align: 'center', headerAlign: 'center' },
    { field: 'modelName', headerName: 'Model', flex: 1, minWidth: 110, align: 'center', headerAlign: 'center' },
    { field: 'productName', headerName: 'Product', flex: 1, minWidth: 200, align: 'center', headerAlign: 'center' },
    { field: 'imeiNo', headerName: 'Serial Number', flex: 1, minWidth: 170, align: 'center', headerAlign: 'center' },
    { field: 'batchNo', headerName: 'Batch', flex: 1, minWidth: 120, align: 'center', headerAlign: 'center' },
    { field: 'purchasePrice', headerName: 'Cost', flex: 1, minWidth: 100, align: 'center', headerAlign: 'center' },
    { field: 'sellingPrice', headerName: 'Price', flex: 1, minWidth: 100, align: 'center', headerAlign: 'center' },
    {
      field: 'receivedDate',
      headerName: 'Received',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (row) => longDate(row.value as string),
    },
    {
      field: 'soldDate',
      headerName: 'Sold',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      // A phone still on the shelf has no sold date; say so rather than leaving a
      // blank cell that reads as missing data.
      renderCell: (row) =>
        row.value ? (
          longDate(row.value as string)
        ) : (
          <Typography variant="caption" color="text.secondary">
            {row.row.status === 'AVAILABLE' ? 'In stock' : '-'}
          </Typography>
        ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (row) => (
        <Chip size="small" variant="outlined" label={readable(row.value as string)} color={statusColour[String(row.value)] || 'default'} />
      ),
    },
  ];

  const download = () =>
    exportToExcel(
      rows.map((row: any) => ({
        Brand: row.brandName,
        Model: row.modelName,
        Product: row.productName,
        'Serial Number': row.imeiNo,
        Batch: row.batchNo,
        Cost: row.purchasePrice,
        Price: row.sellingPrice,
        Received: row.receivedDate,
        Sold: row.soldDate ?? (row.status === 'AVAILABLE' ? 'In stock' : ''),
        Status: readable(row.status),
      })),
      'stock',
      'Stock',
    );

  return (
    <Box>
      <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          placeholder="Any words: serial no, brand, model, colour, RAM, storage..."
          size="small"
          sx={{ width: 300 }}
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          InputProps={{
            startAdornment: (
              <IconButton edge="start" size="small">
                <SearchOutlinedIcon style={{ color: '#bdbdbd' }} />
              </IconButton>
            ),
            endAdornment: searchValue ? (
              <IconButton edge="end" size="small" onClick={() => setSearchValue('')}>
                <ClearIcon />
              </IconButton>
            ) : null,
          }}
        />
        <TextField select label="Status" size="small" sx={{ width: 170 }} value={status} onChange={(event) => setStatus(event.target.value)}>
          {/* Only the two states the software can actually produce. Damaged and
              returned-to-supplier exist in the data model but nothing writes them
              yet, and a filter that can never match is worse than no filter. */}
          <MenuItem value="">All</MenuItem>
          <MenuItem value="AVAILABLE">In stock</MenuItem>
          <MenuItem value="SOLD">Sold</MenuItem>
        </TextField>
        <Chip variant="outlined" label={`${rowCount} handset(s)`} />
        <Box flexGrow={1} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={download} disabled={rows.length === 0}>
          Excel
        </Button>
      </Stack>

      <DataGrid
        columns={columns}
        rows={rows}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={(model: GridPaginationModel) => setPaginationModel(model)}
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        loading={loading}
        sx={{
          '& .MuiDataGrid-columnHeader': {
            fontWeight: 'bold',
            fontSize: '15px',
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
          },
          height: '65vh',
          backgroundColor: 'background.paper',
        }}
      />
    </Box>
  );
};

export default StockReport;
